import React from 'react';
import { trackFieldInteraction, trackPromoCodeSectionOpened } from '../utils/gtmTracking';

/**
 * Helper function to format discount display based on type
 */
const formatDiscountDisplay = (promo) => {
  if (promo.discountInfo?.type === 'trial') {
    return `${promo.discountInfo.amount} ${promo.discountInfo.unit} free`;
  } else if (promo.discountInfo?.type === 'percent') {
    return `${promo.discount}% off`;
  } else {
    // Fixed amount discount
    return `-$${promo.discount?.toFixed(2) || '0.00'}`;
  }
};

/**
 * Applied promo code badge
 */
const AppliedPromoCode = ({ promo, onRemove }) => (
  <div style={{
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    padding: '0.5rem',
    borderRadius: '4px',
    marginBottom: '0.5rem',
    border: '1px solid #4caf50'
  }}>
    <div>
      <span style={{ color: '#4caf50', fontWeight: 'bold' }}>{promo.code}</span>
      <span style={{ color: '#aaa', marginLeft: '0.5rem', fontSize: '0.8rem' }}>
        {formatDiscountDisplay(promo)}
      </span>
    </div>
    <button
      type="button"
      onClick={() => onRemove(promo.code)}
      style={{
        background: 'none',
        border: 'none',
        color: '#ff6b6b',
        cursor: 'pointer',
        fontSize: '1.2rem'
      }}
    >
      &times;
    </button>
  </div>
);

/**
 * Auto-apply indicator
 */
const AutoApplyIndicator = ({ promoInput }) => (
  <div style={{
    marginBottom: '1rem',
    padding: '0.75rem',
    backgroundColor: '#f0f8ff',
    border: '1px solid #add8e6',
    borderRadius: '4px',
    color: '#2c5aa0'
  }}>
    &#x23F3; Applying promo code from URL: {promoInput}
  </div>
);

/**
 * Promo code input and apply button
 */
const PromoCodeInput = ({
  currentPromoInput,
  setCurrentPromoInput,
  promoCodeValidation,
  onApply,
  getFormContext
}) => (
  <div style={{ position: 'relative', display: 'flex', gap: '0.5rem' }}>
    <input
      type="text"
      id="promo-code"
      value={currentPromoInput}
      onChange={(e) => setCurrentPromoInput(e.target.value)}
      onFocus={() => trackFieldInteraction('promoCode', 'focus', null, getFormContext())}
      onBlur={() => trackFieldInteraction('promoCode', 'blur', currentPromoInput, getFormContext())}
      autoComplete="off"
      className="font-mono"
      placeholder="Enter promo code"
      style={{
        maxWidth: '300px',
        borderColor: '#444',
        flex: 1
      }}
    />
    <button
      type="button"
      onClick={onApply}
      disabled={promoCodeValidation.isValidating || !currentPromoInput.trim()}
      className="plan-option billing-period-option"
      style={{
        padding: '0.5rem 1rem',
        cursor: (promoCodeValidation.isValidating || !currentPromoInput.trim()) ? 'not-allowed' : 'pointer',
        color: '#fff',
        fontSize: '1.25rem',
        fontWeight: 600,
        flex: 'none',
        justifyContent: 'center',
        minWidth: '120px',
        opacity: (promoCodeValidation.isValidating || !currentPromoInput.trim()) ? 0.6 : 1
      }}
    >
      {promoCodeValidation.isValidating ? 'Checking...' : 'Apply'}
    </button>
  </div>
);

/**
 * Validation feedback messages
 */
const ValidationFeedback = ({ promoCodeValidation }) => (
  <>
    {/* Error messages */}
    {promoCodeValidation.error && (
      <span className="error" style={{ fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
        {promoCodeValidation.error}
      </span>
    )}

    {/* Success message */}
    {promoCodeValidation.isValid && promoCodeValidation.discount > 0 && (
      <span style={{
        color: '#4caf50',
        fontSize: '0.8rem',
        marginTop: '0.25rem',
        display: 'block'
      }}>
        &#x2713; Promo code applied! {
          promoCodeValidation.discountInfo?.type === 'trial'
            ? `${promoCodeValidation.discountInfo.amount} ${promoCodeValidation.discountInfo.unit} free trial`
            : promoCodeValidation.discountInfo?.type === 'percent'
            ? `${promoCodeValidation.discount}% discount`
            : `Discount: $${promoCodeValidation.discount.toFixed(2)}`
        }
      </span>
    )}
  </>
);

/**
 * Add promo code link button
 */
const AddPromoCodeLink = ({ onClick, getFormContext }) => (
  <button
    type="button"
    className="promo-code-link"
    onClick={() => {
      onClick();
      trackPromoCodeSectionOpened(getFormContext());
    }}
  >
    + Add promo code
  </button>
);

/**
 * Main PromoCodeSection component
 */
const PromoCodeSection = ({
  showPromoCode,
  setShowPromoCode,
  appliedPromoCodes,
  currentPromoInput,
  setCurrentPromoInput,
  promoCodeValidation,
  isAutoApplyingPromo,
  applyPromoCode,
  removePromoCode,
  getFormContext
}) => {
  if (!showPromoCode) {
    return (
      <AddPromoCodeLink
        onClick={() => setShowPromoCode(true)}
        getFormContext={getFormContext}
      />
    );
  }

  return (
    <div className="form-group full-width promo-code-group text-left">
      <label htmlFor="promo-code">Promo code</label>

      {/* Applied promo codes */}
      {appliedPromoCodes.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {appliedPromoCodes.map((promo, index) => (
            <AppliedPromoCode
              key={index}
              promo={promo}
              onRemove={removePromoCode}
            />
          ))}
        </div>
      )}

      {/* Auto-apply indicator */}
      {isAutoApplyingPromo && (
        <AutoApplyIndicator promoInput={currentPromoInput} />
      )}

      {/* New promo code input */}
      <PromoCodeInput
        currentPromoInput={currentPromoInput}
        setCurrentPromoInput={setCurrentPromoInput}
        promoCodeValidation={promoCodeValidation}
        onApply={applyPromoCode}
        getFormContext={getFormContext}
      />

      <ValidationFeedback promoCodeValidation={promoCodeValidation} />
    </div>
  );
};

export default PromoCodeSection;
